import { useState, useEffect, useContext } from "react";
import DeviceDetector from "device-detector-js";
import { rolesConAcceso, adminKey } from "services/config";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

// @mui material components
import {
  ThemeProvider,
  CssBaseline,
  CircularProgress,
  Fade,
  Snackbar,
  Alert,
  Icon,
  Tooltip,
} from "@mui/material";

// Kaizen Dashboard components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

// Kaizen Dashboard examples
import Sidenav from "components/Sidenav";
//import ErrorMessager from "components/ErrorMessager";

// Kaizen Dashboard themes
import theme from "assets/theme";

// Kaizen Dashboard routes
import routes from "routes";

// Kaizen Dashboard contexts
import { useSoftUIController, setMiniSidenav, setOpenConfigurator, MessageManager } from "context";
import { UserContext } from "context/user";

// Querys
import { useQuery, useSubscription, gql } from "@apollo/client";
//import { GET_USER } from "services/login";

// Tools
import { handleResponse } from "services/handleMessages";

// Images
import brand from "assets/images/logo.png";
import { ConfirmarProvider } from "context/ConfirmarContext";

const CHAT_SUBSCRIPTION = gql`
  subscription chat {
    chat
  }
`;

export default function App() {
  // const {data: chatData} = useSubscription(CHAT_SUBSCRIPTION)
  // const userID = localStorage.getItem("user")
  //   ? JSON.parse(localStorage.getItem("user")).user.id
  //   : null;

  const [controller, dispatch] = useSoftUIController();
  const {
    snackbar,
    snackbarMessage,
    snackbarType,
    handleSnackbar,
    closeSnackbar,
    setSnackbarMessage,
  } = useContext(MessageManager);
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const [loadingSpinner, setLoadingSpinner] = useState(true);
  const { logout } = useContext(UserContext);

  // useEffect(() => {
  //   if(chatData){
  //     handleSnackbar(chatData.chat, "error");
  //   }
  // }, [chatData]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    closeSnackbar();
  };

  useEffect(() => {
    setLoadingSpinner(false);
  }, []);

  // const { data, loading, error, refetch } = useQuery(GET_USER);

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const deviceDetector = new DeviceDetector();
    const device = deviceDetector.parse(navigator.userAgent);
    // if (error) {
    //   handleSnackbar(
    //     "Se ha vencido la sesión, deberá iniciar sesión nuevamente. Será redirigido...",
    //     "error"
    //   );
    //   setTimeout(() => {
    //     logout();
    //   }, 3000);
    // }

    if (!user) {
      if (pathname !== "/login") {
        logout();
      }
      return;
    }
    if (user.adminKey && user.adminKey === adminKey) {
      return;
    }

    const userRoutes = routes.filter((route) => route.access?.includes("5"));
    const currentRoute = routes.find((route) => route.route === pathname);

    // if (device.device.type !== "desktop" && pathname !== "/empleado/perfil") {
    //   window.location.href = "/empleado/perfil";
    // }

    if (currentRoute?.access) {
      if (!currentRoute || !currentRoute.access.includes("5")) {
        window.location.href = userRoutes[0].route;
      } else if (pathname === "/login") {
        window.location.href = userRoutes[0].route;
      }
    }
  }, [pathname, rolesConAcceso]);

  // useEffect(() => {
  //   refetch();
  // }, [pathname]);

  //set loading spinner to false

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (
      localStorage.getItem("user") === null &&
      sessionStorage.getItem("user") === null &&
      pathname !== "/login"
    ) {
      logout();
    } else if (
      (localStorage.getItem("user") !== null || sessionStorage.getItem("user") !== null) &&
      pathname === "/login"
    ) {
      logout();
    }
  }, []);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route exact path={route.route} element={route.component} key={"route-" + route.key} />
        );
      }

      return null;
    });

  return loadingSpinner ? (
    <ThemeProvider theme={theme}>
      <ConfirmarProvider>
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh">
          <Fade in={loadingSpinner} appear={true} timeout={1000}>
            <CircularProgress />
          </Fade>
        </SoftBox>
      </ConfirmarProvider>
    </ThemeProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <ConfirmarProvider>
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={snackbar}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert onClose={handleClose} severity={snackbarType} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <CssBaseline />
        {layout === "dashboard" &&
          pathname !== "/login" &&
          pathname !== "/empleado/perfil" &&
          pathname !== "/reciboProveedor" &&
          pathname !== "/reciboCliente" &&
          pathname !== "/reportesProyecto" &&
          pathname !== "/facturapdf" &&
          !pathname.startsWith("/pdf") && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={brand}
                brandName="Kaizen"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
            </>
          )}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </ConfirmarProvider>
    </ThemeProvider>
  );
}
